import { Box, Center, Flex, HStack } from '@chakra-ui/react'
import {
  ColumnDef,
  OnChangeFn,
  SortingState,
  TableOptions,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { useColorMode } from 'uikit/ColorMode/ColorMode'
import { Spinner } from 'uikit/Spinner'
import { ColumnConfig } from './elements/ColumnsConfig'
import { PagePaginator } from './elements/PagePaginator'
import { IsMultiSortFeature } from './features/isMultiSort'

interface ReactTablePagedProps<D extends Record<string, unknown>> extends Omit<
  TableOptions<D>,
  'getCoreRowModel'
> {
  columnsGap?: number
  isLoading: boolean
  totalPages?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  textIsEmpty?: string
  onRowClick?: (row: D) => void
  onSortingChange?: OnChangeFn<SortingState>
  onColumnVisibilityChange?: OnChangeFn<Record<string, boolean>>
  storageId?: string
  isMultiSort?: boolean
  initialState?: {
    sorting: SortingState
  }
}

export const ReactTablePaged = <D extends Record<string, unknown>>(
  props: ReactTablePagedProps<D>
) => {
  const {
    columnsGap,
    isLoading,
    currentPage,
    totalPages,
    textIsEmpty = 'Нет данных',
    onPageChange,
    onSortingChange,
    onRowClick,
    storageId,
    initialState,
    ...rest
  } = props
  const { colorMode } = useColorMode()

  const bodyTrBgHover = colorMode === 'light' ? 'gray.50' : 'gray.700'

  const hiddenColumnsStorage = localStorage.getItem(`hiddenColumns.${storageId}`)
  const isMultiSortStorage = localStorage.getItem(`isMultiSort.${storageId}`)
  const hiddenColumns = (hiddenColumnsStorage && JSON.parse(hiddenColumnsStorage)) || []
  const isMultiSortInit: boolean = (isMultiSortStorage && JSON.parse(isMultiSortStorage)) || false

  const data = rest.data
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting || [])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(hiddenColumns)
  const [isMultiSort, setIsMultiSort] = useState<boolean>(isMultiSortInit)

  useEffect(() => {
    if (storageId) {
      const value = JSON.stringify(columnVisibility)
      localStorage.setItem(`hiddenColumns.${storageId}`, value)
    }
  }, [columnVisibility])

  useEffect(() => {
    if (storageId) {
      const value = JSON.stringify(isMultiSort)
      localStorage.setItem(`isMultiSort.${storageId}`, value)
    }
  }, [isMultiSort])

  const table = useReactTable({
    manualSorting: true,
    sortDescFirst: true,
    ...rest,
    isMultiSortEvent: () => isMultiSort,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: rest.manualSorting === false ? getSortedRowModel() : undefined,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      setSorting(newSorting)
      onSortingChange?.(newSorting)
    },
    onIsMultiSortChange: setIsMultiSort,
    onColumnVisibilityChange: setColumnVisibility,
    _features: [IsMultiSortFeature],
    state: {
      sorting,
      columnVisibility,
      isMultiSort,
    },
  })

  return (
    <Flex direction="column" position="relative" h="full" w="full">
      {storageId && (
        <Box zIndex={11} position="absolute" left="20px" top="2px">
          <ColumnConfig instance={table} />
        </Box>
      )}
      <Box id="scrollableDiv" position="relative" overflow="auto" flexGrow={1} h="full" w="full">
        <Box as="table" w="full">
          <Box
            as="thead"
            zIndex={3}
            position="sticky"
            top={0}
            bg="white"
            borderColor="gray.200"
            bgColor="gray.50"
            fontSize="13px"
            h="36px"
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <Box as="tr" key={headerGroup.id} bgColor="gray.50" _dark={{ bgColor: 'gray.700' }}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Box
                      as="th"
                      key={header.id}
                      fontWeight={500}
                      textAlign="start"
                      px={columnsGap ? columnsGap / 2 + 'px' : '6px'}
                      minW={header.column.columnDef.minSize + 'px'}
                      maxW={header.column.columnDef.maxSize + 'px'}
                      w={header.column.getSize() + 'px'}
                    >
                      <HStack justifyContent="space-between">
                        <Box lineHeight="15px">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Box>
                        <Box
                          display="inline-block"
                          cursor="pointer"
                          onClick={header.column.getToggleSortingHandler()}
                          color="gray.400"
                        >
                          {{
                            asc: (
                              <Box pl="5px">
                                <FaSortUp size={15} />
                              </Box>
                            ),
                            desc: (
                              <Box pl="5px">
                                <FaSortDown size={15} />
                              </Box>
                            ),
                          }[header.column.getIsSorted() as string] ??
                            (header.column.columnDef.enableSorting && <FaSort />)}
                        </Box>
                      </HStack>
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
          {!isLoading && !!data.length && (
            <Box as="tbody">
              {table.getRowModel().rows.map((row) => (
                <Box
                  as="tr"
                  key={row.id}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  _dark={{ borderColor: 'gray.700' }}
                  onClick={() => onRowClick?.(row.original)}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  _hover={{ bg: onRowClick ? bodyTrBgHover : 'transparent' }}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <Box
                        as="td"
                        px={columnsGap ? columnsGap / 2 + 'px' : '6px'}
                        key={cell.id}
                        minW={cell.column.columnDef.minSize + 'px'}
                        maxW={cell.column.columnDef.maxSize + 'px'}
                        w={cell.column.getSize() + 'px'}
                        py="10px"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext()) || (
                          <Box color="gray.400">Нет данных</Box>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {isLoading && (
        <Center position="absolute" top="0" h="50%" w="full">
          <Spinner />
        </Center>
      )}
      {data.length === 0 && !isLoading && (
        <Center position="absolute" top="0" h="50%" w="full" color="gray.400">
          {textIsEmpty}
        </Center>
      )}

      {onPageChange && (
        <Center h="70px" w="full" zIndex={10} py="10px" bgColor="gray100">
          <PagePaginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </Center>
      )}
    </Flex>
  )
}
